import React, { useMemo } from 'react';
import { FIELD_DELIMITER } from 'js/utils/string';
import { getSummaryMetrics } from 'js/utils/question-set';
import TrendSetWithLegend from './TrendSetWithLegend';

function getCapabilityData(userGroupAssessment, summaryMetrics) {
  const { pages = [] } = userGroupAssessment.assessment.document ?? {};
  const assessmentMetrics =
    summaryMetrics.assessments[userGroupAssessment.assessment.id];

  if (!assessmentMetrics) {
    return null;
  }

  if ((userGroupAssessment.assessment?.question_sets?.length ?? 0) === 0) {
    return null;
  }

  const { capabilities, capabilityElements } = pages.reduce(
    (acc, page) => {
      if (page.type !== 'capability-questions') {
        return acc;
      }

      const capabilityElements = Object.fromEntries(
        page.capabilityElements.map((element) => [element.key, element])
      );

      return {
        capabilities: {
          ...acc.capabilities,
          ...page.selectedCapabilities,
        },
        capabilityElements: {
          ...acc.capabilityElements,
          ...capabilityElements,
        },
      };
    },
    { capabilities: {}, capabilityElements: {} }
  );

  const selectedCapabilityIds = Object.keys(capabilities);

  const questions =
    Object.values(assessmentMetrics?.questionSets ?? {}).reduce((acc, qs) => {
      return {
        ...acc,
        ...qs.questions,
      };
    }, {}) ?? {};

  // console.log({ assessmentMetrics, selectedCapabilityIds, questions });

  const metrics = Object.entries(capabilityElements).reduce(
    (acc, [, element]) => {
      const elementKey = element.key;
      return selectedCapabilityIds.reduce((idAcc, capabilityId) => {
        const questionKey = `${elementKey}${FIELD_DELIMITER}${capabilityId}`;

        const questionMetrics = questions[questionKey];

        if (!questionMetrics) {
          // The question wasn't found. This may be a selectedCapability that wasn't removed from the doc
          return idAcc;
        }

        return {
          ...idAcc,
          [elementKey]: {
            ...idAcc[elementKey],
            [capabilityId]: questionMetrics,
          },
        };
      }, acc);
    },
    {}
  );

  return {
    capabilities,
    capabilityElements,
    metrics,
    userGroupAssessment,
  };
}

function getElementTrends(capabilityElements, metrics, capabilities) {
  const assessments = Object.values(metrics);
  const capabilityObjects = Object.values(capabilities);
  const elements = Object.values(capabilityElements);

  const trends = elements.map((element) => {
    // const questionMetrics = metrics[element.key];
    // const capabilityMetrics = Object.fromEntries(
    //   Object.values(capabilities).map((capability) => {
    //     return [capability.id, questionMetrics[capability.id]];
    //   })
    // );

    const capabilityTrends = capabilityObjects.reduce(
      (cAcc, capability) =>
        assessments.reduce((aAcc, assessment) => {
          const jointMetric = assessment.metrics[element.key]?.[capability.id];
          const trendData = (aAcc[capability.id] = aAcc[capability.id] ?? []);
          trendData.push({
            userGroupAssessment: assessment.userGroupAssessment,
            capability,
            data: jointMetric,
          });
          return aAcc;
        }, cAcc),
      {}
    );

    return {
      element,
      capabilityTrends,
    };
  });

  // Object.values(capabilityElements).map((element) => {
  //   Object.fromEntries(
  //     Object.values(capabilities).map((capability) => {
  //       const questionMetrics = metrics[element.key][capability.id];
  //   )

  // });

  return trends;
}

export function CapabilityTrendsChart({
  userGroupAssessments,
  elementChartHeight = 200,
  elementChartWidth = 500,
}) {
  const { capabilities, trends, metrics } = useMemo(() => {
    if (!userGroupAssessments) {
      return {};
    }

    const liveAssessments = userGroupAssessments
      .filter(
        (assessment) => assessment.went_live_at && !assessment.archived_at
      )
      .sort((a, b) => new Date(a.went_live_at) - new Date(b.went_live_at));

    const summaryMetrics = getSummaryMetrics(
      liveAssessments.map((a) => a.assessment)
    );

    const { capabilities, capabilitySets, capabilityElements, metrics } =
      liveAssessments.reduce(
        (acc, userGroupAssessment) => {
          // For each live assessment, I need to get:
          // - The capability elements
          // - The capabilities involved
          // - Metrics for each question, grouped by the capability element/question key

          if (!userGroupAssessment.went_live_at) {
            return acc;
          }

          if (userGroupAssessment.capabilities) {
            userGroupAssessment.capabilities.forEach((capability) => {
              acc.capabilities[capability.id] = capability;
            });
          }

          if (userGroupAssessment.capability_sets) {
            userGroupAssessment.capability_sets.forEach((capabilitySet) => {
              acc.capabilitySets[capabilitySet.id] = capabilitySet;
            });
          }

          // extract the capabilityQuestions

          const metrics = getCapabilityData(
            userGroupAssessment,
            summaryMetrics
          );
          acc.capabilityElements = {
            ...acc.capabilityElements,
            ...metrics.capabilityElements,
          };

          acc.metrics = {
            ...acc.metrics,
            [userGroupAssessment.id]: metrics,
          };

          return acc;
        },
        {
          capabilities: {},
          capabilitySets: {},
          capabilityElements: {},
          metrics: {},
        }
      );

    const trends = getElementTrends(capabilityElements, metrics, capabilities);

    return {
      capabilities,
      capabilitySets,
      capabilityElements,
      metrics,
      trends,
    };

    // const capabilities = assessments.map((assessment) => {})
  }, [userGroupAssessments]);

  return (
    <TrendSetWithLegend
      trends={trends}
      metrics={metrics}
      items={capabilities}
    />
  );
}

export default CapabilityTrendsChart;

// {Object.entries(t.capabilityTrends).map(
//   ([capabilityId, points]) => {
//     return (
//       <div>
//         <h5 style={{ fontSize: '0.9rem' }}>
//           {capabilities[capabilityId].name}
//         </h5>
//         <p style={{ fontSize: '0.6rem' }}>
//           {points.map((p) => p.data?.normalizedScore).join(', ')}
//         </p>
//         {/* <div>{trends.map((t) => t.data)}</div> */}
//       </div>
//     );
//   }
// )}
